import { trierLignes } from '@imp/shared/liste-triee';
import { messageErreur } from '@imp/shared/message-erreur';
import { clientsTrouves } from '@imp/shared/recherche-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Star, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarreRecherche } from '@/components/BarreRecherche';
import { EnteteTriable } from '@/components/EnteteTriable';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useEtatListe } from '@/lib/useEtatListe';
import { listChantiers } from '@/modules/chantiers/api';
import {
  avisGoogle,
  contactDisplayName,
  type Contact,
  deleteContact,
  listContacts,
  versRecherchable,
  type AvisGoogle,
} from '@/modules/clients/api';
import { listDevis } from '@/modules/devis/api';
import { listFactures } from '@/modules/factures/api';

const KIND_LABELS: Record<string, string> = {
  prospect: 'Prospect',
  client: 'Client',
  fournisseur: 'Fournisseur',
  sous_traitant: 'Sous-traitant',
};

// Badge de suivi de l'avis Google (⭐ laissé / demandé / —).
function AvisGoogleBadge({ statut }: { statut: AvisGoogle }) {
  if (statut === 'laisse') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
        <Star className="h-3 w-3 fill-current" /> Avis laissé
      </span>
    );
  }
  if (statut === 'demande') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
        Demandé
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

/**
 * Combien de lignes on dessine d'un coup.
 *
 * La liste entière est chargée — la recherche doit trouver n'importe quel
 * client, pas seulement ceux de la page affichée — mais en dessiner deux
 * mille d'un bloc rend l'écran lent à ouvrir et à faire défiler, surtout sur
 * un téléphone.
 */
const PAS = 200;

// « recent » n'est pas une colonne : c'est l'ordre d'arrivée, le dernier client
// créé en tête — le tri par défaut.
const CLES_TRI = ['recent', 'nom', 'type', 'categorie', 'email', 'telephone'] as const;
type CleTri = (typeof CLES_TRI)[number];
const TRI_DEFAUT = { cle: 'recent' as const, sens: 'desc' as const };

export function ClientsListPage() {
  const [q, setQ] = useState('');
  const [nbAffiches, setNbAffiches] = useState(PAS);
  // `statut` sert ici à choisir quels clients montrer : rien = ceux qui ont un
  // dossier, « tous » = toute la base.
  const liste = useEtatListe<CleTri>(CLES_TRI, TRI_DEFAUT);
  const voirTous = liste.statut === 'tous';
  // Un client « avec un dossier » a au moins un devis, un chantier ou une facture.
  // Les mêmes lectures que les listes Devis, Chantiers et Factures : le cache est
  // partagé, rien n'est lu deux fois.
  const devisQ = useQuery({ queryKey: ['devis'], queryFn: listDevis });
  const facturesQ = useQuery({ queryKey: ['factures'], queryFn: listFactures });
  const chantiersQ = useQuery({ queryKey: ['chantiers'], queryFn: listChantiers });
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['contacts'],
    queryFn: listContacts,
  });
  const queryClient = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: deleteContact,
    onSuccess: () => {
      setConfirmId(null);
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
  const contacts = useMemo((): Contact[] => data ?? [], [data]);
  // Recherche tolérante : accents et casse ignorés, mots dans n'importe quel
  // ordre, téléphone quelle que soit son écriture. Sans recherche, on garde
  // l'ordre d'arrivée (le dernier client créé en tête).
  const filtered = useMemo((): Contact[] => {
    if (q.trim() === '') return contacts;
    const parId = new Map(contacts.map((c) => [c.id, c]));
    return clientsTrouves(contacts.map(versRecherchable), q)
      .map((r) => parId.get(r.id))
      .filter((c): c is Contact => c !== undefined);
  }, [contacts, q]);

  const avecDossier = useMemo(() => {
    const ids = new Set<string>();
    for (const d of devisQ.data ?? []) ids.add(d.contact_id);
    for (const f of facturesQ.data ?? []) ids.add(f.contact_id);
    for (const c of chantiersQ.data ?? []) if (c.contact_id) ids.add(c.contact_id);
    return ids;
  }, [devisQ.data, facturesQ.data, chantiersQ.data]);
  // Tant que les dossiers ne sont pas tous lus, on ne sait pas qui en a un :
  // on montre tout plutôt qu'une liste fausse.
  const dossiersConnus = devisQ.isSuccess && facturesQ.isSuccess && chantiersQ.isSuccess;
  const nbAvecDossier = contacts.filter((c) => avecDossier.has(c.id)).length;

  const recherche = q.trim() !== '';
  // La recherche porte sur TOUS les clients : on cherche quelqu'un dont on se
  // souvient, pas seulement ceux qui ont déjà un dossier.
  const dansLeFiltre =
    recherche || voirTous || !dossiersConnus
      ? filtered
      : filtered.filter((c) => avecDossier.has(c.id));
  // Pendant une recherche, l'ordre de pertinence prime — sauf si l'on a choisi
  // un tri en cliquant une colonne.
  const affiches =
    recherche && liste.tri.cle === 'recent'
      ? dansLeFiltre
      : trierLignes(dansLeFiltre, liste.tri, {
          recent: (c) => c.created_at,
          // Par le NOM DE FAMILLE : on cherche « Dupont », pas « Jean ». Le nom
          // affiché commence par le prénom, il trierait les particuliers par prénom.
          nom: (c) =>
            c.type_personne === 'pro'
              ? (c.raison_sociale ?? '')
              : `${c.nom ?? ''} ${c.prenom ?? ''}`.trim(),
          type: (c) => (c.type_personne === 'pro' ? 'Professionnel' : 'Particulier'),
          categorie: (c) => KIND_LABELS[c.kind] ?? c.kind,
          email: (c) => c.email,
          telephone: (c) => c.telephone,
        });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground">
            Particuliers, professionnels, prospects et fournisseurs.
          </p>
        </div>
        <Button asChild>
          <Link to="/clients/nouveau">
            <Plus className="h-4 w-4" /> Nouveau client
          </Link>
        </Button>
      </div>

      <BarreRecherche
        valeur={q}
        onChange={(v) => {
          setQ(v);
          setNbAffiches(PAS);
        }}
        quoi="un client"
        champs="nom, téléphone, email"
        resultats={filtered.length}
        total={contacts.length}
      />

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Quels clients">
        {(
          [
            [null, 'Avec un devis, un chantier ou une facture', nbAvecDossier],
            ['tous', 'Tous', contacts.length],
          ] as const
        ).map(([cle, libelle, nombre]) => {
          const choisi = (cle === 'tous') === voirTous;
          return (
            <button
              key={cle ?? 'dossier'}
              type="button"
              aria-pressed={choisi && !recherche}
              disabled={recherche || (cle === null && !dossiersConnus)}
              onClick={() => {
                liste.choisirStatut(cle);
                setNbAffiches(PAS);
              }}
              className={`bg-card inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors disabled:opacity-50 ${
                choisi && !recherche ? 'ring-primary border-transparent ring-2' : 'hover:bg-muted/60'
              }`}
            >
              {libelle}
              <span className="tabular-nums opacity-70">
                {cle === null && !dossiersConnus ? '…' : nombre}
              </span>
            </button>
          );
        })}
        {recherche ? (
          <span className="text-muted-foreground text-sm">La recherche porte sur tous les clients.</span>
        ) : null}
        {liste.tri.cle !== 'recent' ? (
          <button
            type="button"
            className="text-primary text-sm hover:underline"
            onClick={() => {
              liste.trierPar('recent', 'desc');
            }}
          >
            Revenir au plus récent d&apos;abord
          </button>
        ) : null}
      </div>

      {del.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {messageErreur(del.error)}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : isError ? (
        <p className="text-destructive">Erreur : {messageErreur(error)}</p>
      ) : affiches.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-muted-foreground">
            {contacts.length === 0 ? 'Aucun client pour le moment.' : 'Aucun résultat.'}
          </p>
          {contacts.length === 0 ? (
            <Button asChild variant="outline">
              <Link to="/clients/nouveau">
                <Plus className="h-4 w-4" /> Créer le premier client
              </Link>
            </Button>
          ) : null}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <EnteteTriable cle="nom" libelle="Nom" tri={liste.tri} onTrier={liste.trierPar} />
                <EnteteTriable cle="type" libelle="Type" tri={liste.tri} onTrier={liste.trierPar} />
                <EnteteTriable cle="categorie" libelle="Catégorie" tri={liste.tri} onTrier={liste.trierPar} />
                <EnteteTriable cle="email" libelle="Email" tri={liste.tri} onTrier={liste.trierPar} />
                <EnteteTriable cle="telephone" libelle="Téléphone" tri={liste.tri} onTrier={liste.trierPar} />
                <th className="px-4 py-3 font-medium">Avis Google</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {affiches.slice(0, nbAffiches).map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-4 py-3 font-medium">
                    <Link to={`/clients/${c.id}`} className="text-primary hover:underline">
                      {contactDisplayName(c)}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.type_personne === 'pro' ? 'Professionnel' : 'Particulier'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{KIND_LABELS[c.kind] ?? c.kind}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.email ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.telephone ?? '—'}</td>
                  <td className="px-4 py-3">
                    <AvisGoogleBadge statut={avisGoogle(c)} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {confirmId === c.id ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Supprimer ?</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          disabled={del.isPending}
                          onClick={() => {
                            del.mutate(c.id);
                          }}
                        >
                          Oui
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setConfirmId(null);
                          }}
                        >
                          Non
                        </Button>
                      </span>
                    ) : (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={`Supprimer ${contactDisplayName(c)}`}
                        onClick={() => {
                          setConfirmId(c.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {affiches.length > nbAffiches ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                {nbAffiches} affichés sur {affiches.length}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setNbAffiches((n) => n + PAS);
                }}
              >
                Afficher les {Math.min(PAS, affiches.length - nbAffiches)} suivants
              </Button>
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}
